import { gql } from '@apollo/client';
import { Button } from '@components/button';
import { Form, FormTextInput } from '@components/form';
import { FormTagsInput } from '@components/form/FormInputs/FormTagsInput';
import { useCreateGraphMutation, useGetAddGraphAvailableChannelsQuery } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useNotifications } from '@notifications/Notifications';
import { navigate } from 'gatsby';
import posthog from 'posthog-js';
import { useState } from 'react';
import * as yup from 'yup';
import { GraphsAddGraphModalAvailableChannels } from './GraphsAddGraphModalAvailableChannels';
import { WorkspaceGraphsAddGraphModalUploadFile } from './GraphsAddGraphModalUploadFile';

gql`
  query GetAddGraphAvailableChannels($workspaceId: String!) {
    getChannels(workspaceId: $workspaceId) {
      channelId
      name
    }
  }
`;

const validationSchema = yup.object({
  name: yup
    .string()
    .trim()
    .max(128, 'Must be less than 128 characters.')
    .required('Name is required'),
  channelId: yup.string().required('Required'),
});

type GraphsAddGraphModalProps = {
  workspaceId: string;
  onClose: () => void;
  refetch?: VoidFunction;
};

export const GraphsAddGraphModal = ({
  onClose,
  workspaceId,
  refetch,
}: GraphsAddGraphModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const [createGraph] = useCreateGraphMutation();
  const { organizationId } = useOrganizationContext();
  const { data: availableChannelsData } = useGetAddGraphAvailableChannelsQuery({
    variables: { workspaceId },
  });
  const availableChannels = availableChannelsData?.getChannels || [];
  const [graphData, setGraphData] = useState<string>();
  const handleCreateGraphClick = createAsyncNotification(
    'Created a new graph & now navigating...',
    async ({
      name,
      description,
      channelId,
      tags,
    }: {
      name: string;
      description: string;
      channelId: string;
      tags: string[];
    }) => {
      await createGraph({
        variables: {
          name,
          description,
          channelId,
          graph: graphData || '',
          staged: false,
          workspaceId,
          tags,
        },
        onCompleted: (data) => {
          if (refetch) {
            void refetch();
          }
          posthog.capture('user_create-graph_submitted');
          onClose();
          navigate(
            `/${organizationId}/workspaces/${workspaceId}/graphs/${data?.createGraph || ''}/`,
          );
        },
        update: (cache) => {
          cache.evict({ fieldName: 'getGraphs' });
        },
      });
    },
  );

  return (
    <Form
      validateOnBlur={false}
      onSubmit={handleCreateGraphClick}
      initialValues={{ name: '', description: '', channelId: '', tags: [] }}
      validationSchema={validationSchema}
    >
      {({ isValid, dirty }) => (
        <Stack>
          <FormTextInput label="Name" name="name" />
          <GraphsAddGraphModalAvailableChannels
            availableChannels={availableChannels}
            name="channelId"
          />
          <FormTextInput
            name="description"
            label="Description"
            multiline
            rows={6}
            inputProps={{ maxLength: 200 }}
          />
          <FormTagsInput name="tags" />
          <WorkspaceGraphsAddGraphModalUploadFile
            onChange={(newGraphData) => setGraphData(newGraphData)}
          />
          <Stack gap={4}>
            <Button
              fullWidth
              type="submit"
              data-cy="Graphs-Add-Graph-Modal-Create-Button"
              disabled={!(isValid && dirty)}
            >
              Create
            </Button>
            <Button
              fullWidth
              variant="secondary"
              data-cy="Graphs-Add-Graph-Modal-Cancel-Button"
              onClick={onClose}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Form>
  );
};
